import { TourStep } from '../../types';
import { TOUR_TARGETS } from './targets';

/**
 * The ending every habitat scenario converges on: meet the animals, follow them, then move on
 * to a habitat that is live (or, with no other pass, an offer to get one).
 *
 * Authored once and spread into each scenario's config. The three tours are separate registry
 * entries so that each keeps its own history, which means step ids only have to be unique
 * *within* a tour — these can safely repeat across all three.
 */

export const TAIL_STEP_IDS = {
  MEET_LEARN: '8-meet-learn',
  FOLLOW_BRANCH: '9-branch',
  FOLLOW: '9-follow',
  NEXT_HABITAT: '10-next-habitat',
} as const;

/**
 * A branch expressed as a step. `autoAdvance` variants are condition-gated and fire immediately,
 * so the first matching one decides where the user goes. Used where the journey branches on
 * state rather than on copy — branching on copy is just two banner variants on one step.
 *
 * Note these transient steps do land in the history stack. The habitat tours author no Go Back
 * buttons, so nothing can return to one and be bounced forward again.
 */
const branch = (id: string, routes: { cond?: string; targetStepId: string }[]): TourStep => ({
  id,
  analytics: { stepNumber: -1 },
  ui: {
    autoAdvance: routes.map((route) => ({ ...route, delayMs: 0 })),
  },
});

/**
 * @param switchHabitatRestarts When true, the 10A "Take me there" button restarts the onboarding on
 *   the habitat it switches to, instead of completing it. Online passes this so a user who keeps
 *   hopping to live habitats is re-onboarded on each; asleep/outage complete as before.
 */
export const habitatTailSteps = ({
  switchHabitatRestarts = false,
}: { switchHabitatRestarts?: boolean } = {}): TourStep[] => [
  {
    id: TAIL_STEP_IDS.MEET_LEARN,
    analytics: { stepNumber: 8, section: 'Meet & Learn' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble: 'Let me introduce you! Every animal here has a story.',
      },
      spotlight: { target: TOUR_TARGETS.HABITAT_TABS, scrollIntoView: true },
      onAppEvent: { event: 'habitat:tab-changed', targetStepId: TAIL_STEP_IDS.FOLLOW_BRANCH },
    },
  },

  // Someone who already follows has nothing left to be asked for, so skip the Follow step.
  branch(TAIL_STEP_IDS.FOLLOW_BRANCH, [
    { cond: 'isFollowingHabitat', targetStepId: TAIL_STEP_IDS.NEXT_HABITAT },
    { targetStepId: TAIL_STEP_IDS.FOLLOW },
  ]),

  {
    id: TAIL_STEP_IDS.FOLLOW,
    analytics: { stepNumber: 9, section: 'Follow' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble: "Click on Follow, and you'll get a ping when they're doing something exciting live on camera!",
      },
      spotlight: { target: TOUR_TARGETS.FOLLOW, scrollIntoView: true },
      onAppEvent: { event: 'habitat:followed', targetStepId: TAIL_STEP_IDS.NEXT_HABITAT },
      // Nothing to ask for if they already follow. The `onAppEvent` above only fires on a fresh
      // follow, so without this a user who is already following — or who follows from elsewhere
      // while sitting here — would be stranded asking them to do what they have done.
      autoAdvance: { cond: 'isFollowingHabitat', delayMs: 0, targetStepId: TAIL_STEP_IDS.NEXT_HABITAT },
    },
  },

  {
    // The designed endings are one step: they differ only in what the banner says and what its
    // button does, which condition-gated banner variants express. An accessible suggestion goes
    // straight there for free; otherwise the explorer-offer eligibility (published into the tour
    // context) chooses between a free checkout, an offer link, or simply wrapping the tour up.
    id: TAIL_STEP_IDS.NEXT_HABITAT,
    analytics: { stepNumber: 10, section: 'Next habitat' },
    ui: {
      banner: [
        {
          // 10A — the suggestion is accessible, so go straight there. No offer needed.
          cond: 'hasAccessibleRecommendedHabitat',
          type: 'guide',
          mascot: 'talking',
          speechBubble: "Who else is out and about? Let's go see the {{ctx.recommendedHabitat.animal}}.",
          buttons: [
            {
              label: 'Take me there',
              navigatePath: '/h/{{ctx.recommendedHabitat.zooSlug}}/{{ctx.recommendedHabitat.slug}}',
              // Online re-runs the onboarding on the habitat we switch to; others just finish.
              ...(switchHabitatRestarts ? { restartTour: true } : { completeTour: true }),
              trackAction: 'switched-habitat',
            },
          ],
        },
        {
          // 10B — eligible, and there is a locked habitat to offer. Buy its single-habitat-day-pass
          // with the offer applied and go straight to Stripe (a custom banner; a navigate button
          // cannot run a checkout mutation).
          cond: 'canOfferCheckout',
          type: 'offer-checkout',
          mascot: 'talking',
          speechBubble:
            "I have an idea! Let's go visit the {{ctx.recommendedHabitat.animal}} and see if they are out and " +
            "about. Since you're new, this habitat visit is on me!",
          buttonLabel: 'Claim my free habitat visit',
          offerId: 'explorer',
        },
        {
          // 10C — eligible, but nothing to name. Send them to the habitats list with the offer
          // pre-applied via `ppto`; the day-pass modal there redeems it at checkout.
          cond: 'canOfferNoRecommended',
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "I have an idea! Let's go visit another habitat and see if they are out and about. " +
            "Since you're new, this habitat visit is on me!",
          buttons: [
            {
              label: 'Claim my free habitat visit',
              navigatePath: '/habitats?ppt=single-habitat-day-pass&ppto=explorer',
              completeTour: true,
              trackAction: 'claimed-offer',
            },
          ],
        },
        {
          // 10D — cannot use the offer (or it failed to load). Just wrap the tour up warmly.
          cond: 'offerIneligible',
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "And that's the tour! You know your way around now — hop between habitats and see who's out and " +
            'about whenever you like.',
          buttons: [
            {
              label: 'Finish the tour',
              completeTour: true,
              trackAction: 'completed-tour',
            },
          ],
        },
      ],
    },
  },
];
